// Feedback: what someone on the site wanted to tell the GM, from the page they were on (migration 0076).
//
// ⚠ TEMPORARY (owner, 2026-09-16): "a feedback button on every page, temporarily, until the system is locked in". Remove
// this, `ui/feedback-button.tsx` and the Feedback card on the campaign sheet together when he says so; leave the table.
import { supabase } from "./supabase";
import { writeOneOrSay, writeOrSay } from "./write-or-say";

export type Feedback = {
  id: string;
  campaign_id: string | null;
  from_user: string | null;
  share_token: string | null;
  page: string;
  body: string;
  build: string | null;
  created_at: string;
  read_at: string | null;
};

/**
 * Send one note. Signed in, the row is the writer's (`from_user = auth.uid()`); on a share link there is no session and
 * the token is what ties it to a table.
 *
 * ⚠ TWO PATHS BECAUSE anon MAY INSERT AND NOTHING ELSE. A signed-in send asks for its row back (`writeOneOrSay` +
 * `.select()`), which is the honest check. An anonymous send cannot — the 0076 grant gives anon no select, and PostgREST
 * refuses `return=representation` without it — so it goes through `writeOrSay`, where an insert the policy declines still
 * arrives as an error (insert refusals raise; it is UPDATE and DELETE that silently match nothing).
 */
export async function sendFeedback(
  { campaignId, shareToken, page, body, build }: { campaignId: string | null; shareToken: string | null; page: string; body: string; build: string | null },
  say: (m: string) => void,
): Promise<boolean> {
  const text = body.trim().slice(0, 4000);
  if (!text) { say("Write a line first — what's wrong, or what would help."); return false; }
  const { data: { session } } = await supabase.auth.getSession();
  const row = { campaign_id: campaignId, share_token: shareToken, page: page.slice(0, 400), body: text, build };
  if (session) {
    return !!(await writeOneOrSay("send that feedback", () => supabase.from("feedback").insert(row).select("id"), say));
  }
  return writeOrSay("send that feedback", () => supabase.from("feedback").insert({ ...row, from_user: null }), say);
}

/** Everything sent about a campaign, newest first. The owner's read; the policy keeps everyone else out. */
export async function listFeedback(campaignId: string, say: (m: string) => void): Promise<Feedback[] | null> {
  const { data, error } = await supabase.from("feedback")
    .select("id,campaign_id,from_user,share_token,page,body,build,created_at,read_at")
    .eq("campaign_id", campaignId).order("created_at", { ascending: false }).limit(200);
  if (error) { say(`Couldn't load the feedback — ${error.message}. Reload the page and try again.`); return null; }
  return (data ?? []) as Feedback[];
}

/**
 * Archive an addressed note, or bring it back (asked 2026-09-23: "a way to move these to an archive once the issue is
 * addressed"). ⚠ REUSES `read_at` — the owner has a column-level update grant on it (0076) and nothing else reads it, so
 * "addressed/archived" is the same stamp, no migration. `null` restores it to the active list.
 */
export async function setFeedbackArchived(id: string, archived: boolean, say: (m: string) => void): Promise<boolean> {
  return !!(await writeOneOrSay(archived ? "archive that" : "bring that back",
    () => supabase.from("feedback").update({ read_at: archived ? new Date().toISOString() : null }).eq("id", id).select("id"), say));
}
